import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { GridActions, GridService } from '@core/services';
import { remToPx } from '@util/layout';

import { CustomerLookupResponseRow } from '#types/customer';
import { Filters } from '#types/grid';

import { AuthService } from '@auth/services';
import { CONSTANTS } from '@customer/constants';
import { CustomerService } from '@customer/services';

@Component({
  selector: 'z-customer-lookup-dialog',
  templateUrl: './customer-lookup-dialog.component.html',
  styleUrls: ['./customer-lookup-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomerLookupDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject<void>();

  form: FormGroup;

  custid: string;
  name: string;

  selectedRow: CustomerLookupResponseRow;

  matchTypes: Record<string, string> = {};

  container: HTMLDivElement;
  containerParent: HTMLDivElement;
  containerFilters: HTMLDivElement;
  containerResizeObserver: ResizeObserver;

  lookupId = 'customer-lookup-dialog';

  gridId = CONSTANTS.GRID_CUSTOMER_LOOKUP_DIALOG;

  constructor(
    private store: Store,
    private customerService: CustomerService,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private gridService: GridService,
    private authService: AuthService,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    this.form = new FormGroup({
      custid: new FormControl(),
      name: new FormControl(),
    });

    this.gridService
      .selectedRow$<CustomerLookupResponseRow>(this.gridId)
      .pipe(
        filter((x) => !!x),
        takeUntil(this.destroy$)
      )
      .subscribe((selectedRow) => {
        this.selectedRow = selectedRow.data;
        this.custid = this.selectedRow.custid;
        this.name = this.selectedRow.name;

        if (selectedRow.selectionType === 'double') {
          this.onSelectClick();
        }
      });

    // close dialog if no longer authenticated
    this.authService.authenticated$
      .pipe(
        filter((authenticated) => !authenticated),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.onCancelClick();
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
    this.destroyResizeObserver();
  }

  ngAfterViewInit() {
    this.initResizeObserver();
  }

  initResizeObserver() {
    this.containerResizeObserver = new ResizeObserver(() => {
      this.ngZone.run(() => {
        const header: HTMLDivElement = this.containerParent.querySelector('.p-dialog-header');
        const filters: HTMLDivElement = this.container.querySelector('.filters');
        const footer: HTMLDivElement = this.container.querySelector('.p-dialog-footer');
        const paginator: HTMLDivElement = this.container.querySelector('.p-paginator');

        let offset = 0;

        if (header) {
          offset += Math.floor(header.offsetHeight);
        }

        if (filters) {
          offset += Math.floor(filters.offsetHeight);
        }

        if (footer) {
          offset += Math.floor(footer.offsetHeight);
        }

        if (paginator) {
          offset += Math.floor(paginator.offsetHeight);
        }

        this.customerService.customerLookupDialogDimensions$.next({
          container: {
            width: Math.floor(this.containerParent.offsetWidth),
            height: Math.floor(this.containerParent.offsetHeight),
          },
          grid: {
            width: Math.floor(this.containerParent.offsetWidth) - remToPx('2rem'),
            height: Math.floor(this.containerParent.offsetHeight) - offset,
          },
        });
      });
    });

    try {
      this.container = document.querySelector(`.${this.lookupId}`);

      if (this.container) {
        this.containerParent = this.container.closest(`.lookup-dialog`);

        if (this.containerParent) {
          this.containerResizeObserver.observe(this.containerParent);
        }

        this.containerFilters = this.container.querySelector('.filters');

        if (this.containerFilters) {
          this.containerResizeObserver.observe(this.containerFilters);
        }
      }
    } catch {}
  }

  destroyResizeObserver() {
    try {
      if (this.containerResizeObserver && this.containerParent) {
        this.containerResizeObserver.unobserve(this.containerParent);
      }

      if (this.containerResizeObserver && this.containerFilters) {
        this.containerResizeObserver.unobserve(this.containerFilters);
      }
    } catch {}
  }

  onSelectClick() {
    this.ref.close({ custid: this.custid, name: this.name, selectedRow: this.selectedRow });
  }

  onCancelClick() {
    this.ref.close(undefined);
  }

  onMatchTypeChange(field: string, matchType: string) {
    this.matchTypes[field] = matchType;
  }

  applyFilters() {
    const filters: Filters = {};

    if (this.form.get('custid').value) {
      filters.custid = { value: this.form.get('custid').value, matchMode: this.matchTypes.custid ?? 'equals' };
    }

    if (this.form.get('name').value) {
      filters.name = { value: this.form.get('name').value, matchMode: this.matchTypes.name ?? 'startsWith' };
    }

    this.store.dispatch(GridActions.SetFilters({ id: this.gridId, filters }));
  }

  reset(): void {
    this.form.setValue({
      custid: null,
      name: null,
    });
  }

  isInvalid(formControlName: string) {
    return !this.form.controls[formControlName].pristine && this.form.controls[formControlName].invalid;
  }
}
